import StringExpression from 'utils/filter/expressions/StringExpression';
import NumberExpression from 'utils/filter/expressions/NumberExpression';
import { IndigoAppContextType } from 'app/AppContext';
import { EnumMap } from '@instinet-analytics/core-lib';
import {
    ColumnDefinitionsDictionary,
    ValueType,
} from '@instinet-analytics/semantic-ui-components';
import isNil from 'lodash/isNil';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyEntity = any;

type Evaluator = (entity: AnyEntity) => boolean;

export default function buildQuickSearchFunction<T extends string>(
    columnDefinitions: ColumnDefinitionsDictionary<T>,
    searchText: string,
    columns: T[],
    appContext: IndigoAppContextType
) {
    const evaluators = getEvaluators(columnDefinitions, searchText, appContext);

    return (entity: AnyEntity): boolean => {
        if (!searchText) {
            return true;
        }

        return evaluate(entity, evaluators);
    };

    function getEvaluators(
        columnDefinitions: ColumnDefinitionsDictionary<T>,
        searchText: string,
        appContext: IndigoAppContextType
    ): Evaluator[] {
        const evaluators: Evaluator[] = [];

        const stringExpression = new StringExpression(searchText);
        const numberExpressions: { [key: string]: NumberExpression } = {};
        const numericSearchText = toNumericSearchText(searchText);
        const percentageSearchText = toPercentageSearchText(searchText);

        for (const columnName of columns) {
            const column = columnDefinitions[columnName];
            if (!column) {
                continue;
            }

            const dataField = column.field;

            if (
                column.valueType === ValueType.STRING ||
                column.textualQuickSearch
            ) {
                evaluators.push(
                    getStringEvaluator(dataField, stringExpression)
                );
            }
            if (column.valueType === ValueType.ENUM) {
                evaluators.push(
                    getEnumEvaluator(
                        dataField,
                        stringExpression,
                        column.enumMap
                    )
                );
            }
            if (
                column.valueType === ValueType.NUMBER ||
                column.valueType === ValueType.CURRENCY
            ) {
                const valueToSearch = column.isPercentage
                    ? percentageSearchText !== undefined
                        ? percentageSearchText
                        : numericSearchText
                    : numericSearchText;

                const exchangeRate =
                    column.valueType === ValueType.CURRENCY
                        ? appContext.currencyFormat.exchangeRate || 1
                        : 1;

                if (valueToSearch !== undefined) {
                    const precision = column.precision ?? 0;
                    const key = `${precision}-${exchangeRate}`;
                    let numberExpression = numberExpressions[key];
                    if (!numberExpression) {
                        numberExpression = new NumberExpression(
                            valueToSearch,
                            precision,
                            exchangeRate
                        );
                        numberExpressions[key] = numberExpression;
                    }

                    evaluators.push(
                        getNumericEvaluator(dataField, numberExpression)
                    );
                }
            }
        }

        return evaluators;
    }

    function evaluate(row: AnyEntity, evaluators: Evaluator[]) {
        for (const evaluator of evaluators) {
            if (evaluator(row)) {
                return true;
            }
        }
        return false;
    }

    function getStringEvaluator(
        dataField: string,
        stringExpression: StringExpression
    ) {
        return (row: AnyEntity) => {
            const value = row[dataField];
            const item = isNil(value)
                ? ''
                : typeof value === 'string'
                ? value
                : value.toString();
            if (item === '') {
                return false;
            }
            return stringExpression.testContains(item);
        };
    }

    function getEnumEvaluator(
        dataField: string,
        stringExpression: StringExpression,
        enumMap?: EnumMap
    ) {
        return (row: AnyEntity) => {
            const item = row[dataField] as string | number;
            if (!enumMap) {
                return false;
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const value = (enumMap as any)[item];
            if (value === undefined) {
                return false;
            }
            return stringExpression.testContains(value);
        };
    }

    function getNumericEvaluator(
        dataField: string,
        expression: NumberExpression
    ) {
        return (row: AnyEntity) => {
            const item = row[dataField] as number;
            if (item === undefined || item === null || isNaN(item)) {
                return false;
            }

            return expression.testEqual(item);
        };
    }

    function toNumericSearchText(searchText: string): number | undefined {
        if (searchText === undefined || searchText === null) {
            return undefined;
        }
        const stripped = searchText.replace(',', '');
        const numeric = Number(stripped);
        if (isNaN(numeric)) {
            return undefined;
        }
        return numeric;
    }

    function toPercentageSearchText(searchText: string): number | undefined {
        if (searchText === undefined || searchText === null) {
            return undefined;
        }
        if (searchText.endsWith(`%`)) {
            return toNumericSearchText(
                searchText.slice(0, searchText.length - 1)
            );
        }
        return toNumericSearchText(searchText);
    }
}
